import * as React from 'react';

import { getImageData } from 'lib/get-image-data';
import Image from 'lib/image-container';
import { MediaQueryWidth } from 'lib/use-media-query';
import useScrollbarWidth from 'lib/use-scrollbar-width';

import Button from 'components/button/button';
import { Columns, ColumnsItem } from 'components/columns';
import { ColumnsSize } from 'components/columns/columns-item';
import { CtaBox, CtaBoxActions, CtaBoxContent, CtaBoxHeader, CtaBoxText } from 'components/cta-box';
import PageSection, { PageSectionProps } from 'components/page/page-section';
import { Header2 } from 'components/typography';

import {
  COLUMNS_GAP_MEDIUM,
  IMAGE_MAX_WIDTH_1,
  PAGE_PADDING_LARGE,
  PAGE_PADDING_MEDIUM,
  PAGE_PADDING_SMALL,
} from './page-section-cta.enums';

type Props = {
  action?: {
    onClick?: () => void;
    href?: string;
    text: string;
  };
  title?: string;
  titleElement?: React.ReactNode | string;
  text: React.ReactNode | string;
  image?: {
    src: string;
    objectPosition: string;
    objectFit?: string;
    // position in desktop layout, 'end' by default
    inlinePosition?: 'start' | 'end';
    hasLoadPriority?: boolean;
  };
  imageText?: React.ReactNode | string;
  imageTextPosition?: string;
  color: PageSectionProps['color'];
  columnLeft?: {
    size: ColumnsSize;
    contentBefore?: React.ReactNode;
    content?: React.ReactNode;
  };
  columnRight?: {
    size: ColumnsSize;
    content?: React.ReactNode;
  };
};

const PageSectionCta: React.FC<Props> = ({
  image,
  imageText,
  imageTextPosition,
  action,
  title,
  titleElement,
  text,
  color,
  columnLeft,
  columnRight,
}) => {
  const scrollbarWidth = useScrollbarWidth(true);
  const blurDataUrl = image ? getImageData(image.src).blurDataUrl : undefined;
  const leftImageBlock = (
    <div
      className="page-section__picture"
      style={
        blurDataUrl
          ? {
              backgroundImage: `url("${blurDataUrl}")`,
              backgroundSize: 'cover',
              backgroundPosition: image && image.objectPosition,
            }
          : undefined
      }
    >
      {image ? (
        <Image
          priority={image.hasLoadPriority}
          width={600}
          height={620}
          style={{
            // @ts-ignore
            objectFit: image.objectFit || 'cover',
            objectPosition: image.objectPosition,
          }}
          quality={75}
          src={image.src}
          alt=""
          placeholder={blurDataUrl ? 'blur' : undefined}
          blurDataURL={blurDataUrl}
          sizes={`
            (max-width: ${MediaQueryWidth['desktop-sm']}px) calc(100vw - 2 * ${PAGE_PADDING_SMALL} - ${scrollbarWidth}),
            (max-width: ${MediaQueryWidth['tablet-lg']}px) calc((100vw - 2 * ${PAGE_PADDING_MEDIUM} - ${COLUMNS_GAP_MEDIUM} - ${scrollbarWidth}) / 2),
            (max-width: ${MediaQueryWidth['content-max']}px) calc((100vw - 2 * ${PAGE_PADDING_LARGE} - ${COLUMNS_GAP_MEDIUM} - ${scrollbarWidth}) / 2),
            ${IMAGE_MAX_WIDTH_1}
            `}
        />
      ) : null}
    </div>
  );

  return (
    <PageSection color={color} extraClass="section-cta" padding="block" inner={true}>
      <Columns
        // equal={true}
        hasMobileLayout={true}
        isMobileOrderInverted={image && image.inlinePosition !== 'start'}
      >
        <ColumnsItem verticalAlign="stretch" size={columnLeft?.size}>
          <CtaBox align={'start'}>
            <CtaBoxContent width="stretch">
              {columnLeft?.contentBefore}
              {leftImageBlock}
              {columnLeft?.content}
              {imageTextPosition === 'bottom' && imageText ? (
                <div>{imageText}</div>
              ) : (
                <>{imageText}</>
              )}
            </CtaBoxContent>
          </CtaBox>
        </ColumnsItem>

        <ColumnsItem size={columnRight?.size}>
          <CtaBox align="start">
            {title ? (
              <CtaBoxHeader>
                <Header2>{title}</Header2>
              </CtaBoxHeader>
            ) : null}
            {titleElement ? <CtaBoxHeader>{titleElement}</CtaBoxHeader> : null}

            <CtaBoxText>{text}</CtaBoxText>

            {action?.href ? (
              <CtaBoxActions align="start">
                <Button
                  href={action.href}
                  text={action.text}
                  // eslint-disable-next-line react/jsx-handler-names
                  onClick={action.onClick}
                />
              </CtaBoxActions>
            ) : null}
            {columnRight?.content}
          </CtaBox>
        </ColumnsItem>
      </Columns>
    </PageSection>
  );
};

export default PageSectionCta;
